'use client'

import { Bell, LogOut, Smartphone, UserRound } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { Dispatch, SetStateAction, useState } from 'react'

import { Dialog, DialogContent } from '@/components/ui/dialog'
import { NotificationTab } from '@/components/user-preferences/notification-tab'
import { PwaTab } from '@/components/user-preferences/pwa-tab'

import { useSignOut } from '@/hooks/use-sign-out'

import { AlertModal } from '../modals/alert-modal'

import AccountTab from './account-tab/account-tab'

interface Props {
  isOpen: boolean
  onClose: Dispatch<SetStateAction<boolean>>
}

const UserPreferencesDialog = ({ isOpen, onClose }: Props) => {
  const t = useTranslations('UserPreferences')
  const [activeTab, setActiveTab] = useState('account')
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const signOut = useSignOut()

  const tabs = [
    {
      id: 'account',
      label: t('accounts.title'),
      icon: <UserRound className="mr-2 h-4 w-4 shrink-0" />,
      component: <AccountTab />,
    },
    {
      id: 'notification',
      label: t('notifications.title'),
      icon: <Bell className="mr-2 h-4 w-4 shrink-0" />,
      component: <NotificationTab />,
    },
    {
      id: 'pwa',
      label: t('pwa.title'),
      icon: <Smartphone className="mr-2 h-4 w-4 shrink-0" />,
      component: <PwaTab />,
    },
  ].filter(Boolean) as {
    id: string
    label: string
    icon: React.ReactNode
    component: React.ReactNode
  }[]

  // Get the active tab component, defaulting to AccountTab if not found
  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component || <AccountTab />

  // Handle logout confirmation
  const onLogoutModalConfirm = async () => {
    await signOut()
  }

  return (
    <>
      {/* Main dialog */}
      <Dialog open={isOpen} onOpenChange={(open) => onClose(!open)}>
        <DialogContent className="flex h-[90%] max-w-[90%] flex-col gap-0 overflow-hidden p-0 md:h-[70%] md:flex-row lg:h-3/5 lg:max-w-[800px] xl:h-[615px]">
          {/* Sidebar */}
          <div className="flex justify-between overflow-x-auto border-r border-base-border bg-base-sidebar-background p-3 pe-12 dark:bg-sidebar-foreground md:w-52 md:flex-col md:overflow-x-visible md:pe-3 lg:w-64">
            <ul className="flex gap-2 md:w-full md:flex-col">
              {tabs.map((tab) => (
                <li
                  key={tab.id}
                  className={`flex cursor-pointer items-center rounded-lg p-2 text-sm font-medium hover:bg-base-sidebar-accent ${
                    activeTab === tab.id
                      ? 'bg-base-sidebar-accent text-base-sidebar-foreground dark:text-base-sidebar-accent-foreground '
                      : ''
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <span className="text-nowrap">{tab.label}</span>
                </li>
              ))}
            </ul>
            <div
              className="flex cursor-pointer items-center rounded-lg p-2 text-sm font-normal hover:bg-base-sidebar-accent md:w-full"
              onClick={() => setIsLogoutModalOpen(true)}
            >
              <LogOut className="mr-2 h-4 w-4" /> <span className="text-nowrap">{t('logout.title')}</span>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-x-hidden">{ActiveComponent}</div>
        </DialogContent>
      </Dialog>

      {/* Logout confirmation modal */}
      <AlertModal
        title={t('logout.titleDialog')}
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={() => onLogoutModalConfirm()}
        isShowDescription={false}
        titleButtonSubmit={t('logout.title')}
      />
    </>
  )
}

export default UserPreferencesDialog
